import { fetchWithAuth } from '@/services/api/apiConfig';
import { CalComUser } from '@/services/api/clients/calcom';
import type { FlowATSDestination } from '@/services/api/clients/atsIntegrations';
import type { Icon } from '@phosphor-icons/react';

// Backend API Types (matching the API documentation)

export type PublishBlockReason = 'fewer_than_two_choices' | 'choice_missing_score';

export type FollowupBand = 'high' | 'medium' | 'low';

export interface InvalidMultipleChoiceQuestion {
  question_id: number;
  role_id: number;
  role_name: string;
  question_name: string;
  choices_count: number;
  // Backend tags each invalid question with the specific rule it violated so
  // the modal can render an accurate hint instead of a one-size-fits-all
  // "needs at least 2" string. Optional for forward-compat with older payloads.
  reason?: PublishBlockReason;
  unscored_choices?: string[];
  // SCRUM-1816: when the blocker is a follow-up rather than the parent
  // question, exactly one of these is set. ``choice_label`` points at the
  // parent choice that owns a per-choice follow-up; ``followup_band``
  // points at a band-level (``*_response_followup``) follow-up.
  followup_band?: FollowupBand;
  choice_label?: string;
}

/**
 * Thrown when the publish endpoint rejects with HTTP 400 because one or more
 * enabled multiple-choice questions have fewer than 2 choices. Carries the
 * structured payload so the UI can list the specific offending questions.
 */
export class PublishBlockedError extends Error {
  invalidQuestions: InvalidMultipleChoiceQuestion[];

  constructor(message: string, invalidQuestions: InvalidMultipleChoiceQuestion[]) {
    super(message);
    this.name = 'PublishBlockedError';
    this.invalidQuestions = invalidQuestions;
  }
}

/**
 * Translate a publish error from `fetchWithAuth` into a typed error the UI
 * can branch on. `fetchWithAuth` re-throws as `API call failed: <body>`, so
 * we strip the prefix and try to parse the JSON body. Exported for tests.
 */
export function normalizePublishError(error: unknown): Error {
  const errorMessage =
    error instanceof Error ? error.message : 'Failed to publish flow to live agent';

  // Prefer the raw body stashed on the Error by fetchWithAuth — readErrorMessage
  // summarizes JSON responses down to `parsed.error` and drops the rest, which
  // hides the `invalid_multiple_choice_questions` array we need here. Fall back
  // to scanning the message for an embedded JSON object so older callers (and
  // tests that synthesize Errors directly) still work.
  const attachedBody = (error as { body?: unknown } | null)?.body;
  let jsonText: string | null = null;
  if (typeof attachedBody === 'string' && attachedBody.trim().startsWith('{')) {
    jsonText = attachedBody;
  } else {
    const jsonStart = errorMessage.indexOf('{');
    if (jsonStart !== -1) jsonText = errorMessage.slice(jsonStart);
  }

  if (!jsonText) return new Error(errorMessage);

  let parsed: { error?: string; invalid_multiple_choice_questions?: unknown } | null = null;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    return new Error(errorMessage);
  }

  if (parsed && Array.isArray(parsed.invalid_multiple_choice_questions)) {
    return new PublishBlockedError(
      parsed.error ?? 'Cannot publish flow.',
      parsed.invalid_multiple_choice_questions as InvalidMultipleChoiceQuestion[]
    );
  }
  if (parsed?.error) return new Error(parsed.error);
  return new Error(errorMessage);
}

export type FlowType = 'hiring' | 'intake';

export type ScoreLevel = 'Low' | 'Medium' | 'High' | 'none';

/**
 * Normalize scoring value to ensure proper capitalization.
 * Backend expects 'Low', 'Medium', 'High', or 'none' (with capital first letter).
 * This function handles cases where the database might have lowercase values.
 */
function normalizeScoring(score: ScoreLevel): ScoreLevel {
  if (score === 'none') return 'none';
  // Capitalize first letter, lowercase the rest
  const normalized = score.charAt(0).toUpperCase() + score.slice(1).toLowerCase();
  return normalized as ScoreLevel;
}

export type IconType =
  | 'UserIcon'
  | 'EnvelopeIcon'
  | 'PhoneIcon'
  | 'IdentificationBadgeIcon'
  | 'IdentificationCardIcon'
  | 'ListHeartIcon'
  | 'ShieldCheckIcon'
  | 'ClockIcon'
  | 'HeartIcon'
  | 'FirstAidKitIcon'
  | 'UserListIcon'
  | 'CalendarIcon'
  | 'HandbagIcon'
  | 'StethoscopeIcon'
  | 'BroomIcon'
  | 'CookingPotIcon'
  | 'UsersIcon'
  | 'BriefcaseIcon'
  | 'MoneyIcon'
  | 'ClipboardTextIcon'
  | 'UploadSimpleIcon'
  | 'QuestionIcon';

export interface Flow {
  id: number;
  type: FlowType;
  location: string | null;
  organization: number | null;
  is_landing_flow: boolean;
  created_by: number;
  preview_agent: number | null;
  agent_uuid?: string | null; // UUID of the associated agent (if flow has an associated agent)
  created_at: string;
  updated_at: string;
  schedule: Schedule;
  rejection: Rejection;
  qualified_info: QualifiedInfo;
  has_unpublished_changes: boolean;
  is_active: boolean;
  published_is_active: boolean;
  ats_destinations?: FlowATSDestination[];
}

export interface Schedule {
  id: number;
  flow: number;
  score: number; // 0-100
  exact_response: boolean;
  send_to_dash: boolean;
  send_to_apploi: boolean;
  schedule_meeting: boolean;
  message: string; // HTML
  chosen_cal_users: string[]; // CalComProfile public IDs
  chosen_cal_users_details?: CalComUser[];
  created_at: string;
  updated_at: string;
}

export interface Rejection {
  id: number;
  flow: number;
  location: string;
  message: string; // HTML
  exact_response: boolean;
  send_to_dash: boolean;
  send_to_apploi: boolean;
  schedule_anyway: boolean;
  chosen_cal_users: string[]; // CalComProfile public IDs
  chosen_cal_users_details?: CalComUser[];
  created_at: string;
  updated_at: string;
}

export interface QualifiedInfo {
  id: number;
  roles: Role[];
}

export interface Role {
  id: number;
  name: string;
  enabled: boolean;
  description: string; // HTML
  order: number;
  Icon?: string;
  information: RoleInformationType[];
  questions_count: number;
}

export type AnswerType = 'open_ended' | 'multiple_choice' | 'document_upload';

// SCRUM-1730: each multiple-choice option carries its own score so the
// chosen option's score becomes the question score. ``ChoiceScore`` and
// ``MultipleChoiceOption`` mirror the persisted JSON shape on the backend.
export type ChoiceScore = 'No score' | 'High' | 'Medium' | 'Low';
export interface MultipleChoiceOption {
  label: string;
  score: ChoiceScore;
  // SCRUM-1816: each MC choice can carry its own follow-up question that
  // fires when the lead selects this specific choice. The follow-up is a
  // leaf — its own ``multiple_choice`` form (label list) does NOT carry
  // per-choice follow-ups, enforcing a single layer of branching.
  followup?: FollowUpQuestion | null;
}

export interface FollowUpQuestion {
  type: AnswerType;
  text: string;
  upload_instructions?: string;
  // For ``multiple_choice`` follow-ups: the label list rendered as the
  // clickable widget. Per the one-layer rule, these entries are
  // ``{label, score}`` only — they never carry their own ``followup``.
  choices?: MultipleChoiceOption[];
}

/**
 * Canonical labels for the contact-info questions the backend templates onto
 * every new role. Questions whose `question` text matches one of these are
 * treated as required (non-deletable, no response-assessment UI).
 *
 * The trailing entries are legacy short labels that pre-date the BE rename in
 * alita_core#833; keep them until existing roles are migrated so the UI still
 * recognises older rows during rollout.
 */
export const REQUIRED_QUESTION_LABELS = [
  'What is your full name?',
  'What is your email address?',
  'What is your phone number?',
  // Legacy labels — remove once BE migration completes.
  'Full Name',
  'Name',
  'Email',
  'Phone',
] as const;

export function isRequiredQuestionLabel(label: string | null | undefined): boolean {
  return !!label && (REQUIRED_QUESTION_LABELS as readonly string[]).includes(label);
}

export interface RoleInformationType {
  id: number;
  scoring: ScoreLevel;
  Icon: string;
  question: string;
  enabled: boolean;
  answer_type: AnswerType;
  upload?: boolean;
  description: string; // HTML
  high_response?: string;
  medium_response?: string;
  low_response?: string;
  high_response_followup?: FollowUpQuestion | null;
  medium_response_followup?: FollowUpQuestion | null;
  low_response_followup?: FollowUpQuestion | null;
  order: number;
  role: number;
  choices?: MultipleChoiceOption[];
}

// Request/Response Types

export interface CheckFlowResponse {
  exists: boolean;
  flow_id?: number;
  flow_type?: FlowType;
  location_id?: string;
  organization_id?: number;
}

export interface CreateFlowRequest {
  type: FlowType;
}

export interface UpdateScheduleRequest {
  score?: number;
  exact_response?: boolean;
  send_to_dash?: boolean;
  send_to_apploi?: boolean;
  schedule_meeting?: boolean;
  message?: string;
  chosen_cal_users?: string[];
  chosen_cal_users_details?: CalComUser[];
}

export interface UpdateRejectionRequest {
  message?: string;
  exact_response?: boolean;
  send_to_dash?: boolean;
  send_to_apploi?: boolean;
  schedule_anyway?: boolean;
  chosen_cal_users?: string[];
  chosen_cal_users_details?: CalComUser[];
}

export interface CreateRoleRequest {
  name: string;
  enabled?: boolean;
  description?: string;
  order?: number;
  Icon?: string;
}

export interface UpdateRoleRequest {
  name?: string;
  enabled?: boolean;
  description?: string;
  order?: number;
  Icon?: string;
}

export interface CreateQuestionRequest {
  scoring: ScoreLevel;
  Icon?: string;
  question: string;
  enabled: boolean;
  answer_type: AnswerType;
  upload?: boolean;
  description: string;
  high_response?: string;
  medium_response?: string;
  low_response?: string;
  high_response_followup?: FollowUpQuestion | null;
  medium_response_followup?: FollowUpQuestion | null;
  low_response_followup?: FollowUpQuestion | null;
  order?: number;
  choices?: MultipleChoiceOption[];
}

export interface UpdateQuestionRequest {
  scoring?: ScoreLevel;
  Icon?: string;
  question?: string;
  enabled?: boolean;
  answer_type?: AnswerType;
  upload?: boolean;
  description?: string;
  high_response?: string;
  medium_response?: string;
  low_response?: string;
  high_response_followup?: FollowUpQuestion | null;
  medium_response_followup?: FollowUpQuestion | null;
  low_response_followup?: FollowUpQuestion | null;
  order?: number;
  role?: number;
  choices?: MultipleChoiceOption[];
}

export interface SaveFlowRequest {
  flow_id: number;
  preview_agent_id: number;
}

export interface PublishFlowRequest {
  flow_id: number;
  agent_id: string | number; // Can be UUID string or integer ID
}

export interface SaveFlowResponse {
  message: string;
  preview_agent_id?: number;
  flow_type: FlowType;
}

export interface PublishFlowResponse {
  message: string;
  agent_uuid?: string;
  flow_type: FlowType;
}

export interface FlowConfig {
  id: number;
  type: 'hiring' | 'intake';
  schedule?: {
    score: number;
    message: string;
    exact_response: boolean;
    send_to_dash: boolean;
    chosen_cal_users: string[];
  };
  rejection?: {
    message: string;
    exact_response: boolean;
    send_to_dash: boolean;
    schedule_anyway: boolean;
    chosen_cal_users?: string[];
  };
  qualified_info?: {
    roles: Array<{
      id: number;
      name: string;
      lead_type: string;
      order: number;
    }>;
  };
}

export interface QualificationData {
  qualification_complete: boolean;
  lead_type: string;
  score: number;
  name: string;
  email: string;
  phone: string;
  summary?: string;
  leadId?: string;
  responses?: Array<{
    question_id: string;
    question_text: string;
    question_type: string;
    lead_type: string;
    answer: string;
    pass_fail: 'pass' | 'fail';
  }>;
  agent_uuid?: string;
  organization_id?: number;
}

export interface ListItem {
  id: string;
  name: string;
  questions: number;
  enabled: boolean;
  icon?: string;
}

export interface CardItem {
  id: string;
  label: string;
  description?: string;
  score: string;
  active: boolean;
  questionType?: AnswerType;
  uploadRequired?: boolean;
  icon?: Icon;
  highResponse?: string;
  mediumResponse?: string;
  lowResponse?: string;
  highResponseFollowup?: FollowUpQuestion | null;
  mediumResponseFollowup?: FollowUpQuestion | null;
  lowResponseFollowup?: FollowUpQuestion | null;
  isRequired?: boolean;
  answerType?: AnswerType;
  // SCRUM-1730: each MC option carries its own score; consumers always
  // see ``MultipleChoiceOption[]`` after ``normalizeChoices`` is applied.
  choices?: MultipleChoiceOption[];
}

// API Error Types

export interface ApiError {
  error?: string;
  [key: string]: string[] | string | undefined;
}

// Flow Builder API Client

const flowBuilder = {
  /**
   * Check if a flow exists for a location and flow type
   */
  async checkFlow(locationId: string, flowType: FlowType): Promise<CheckFlowResponse> {
    try {
      return await fetchWithAuth(`rest/flow-builder/check-flow/${locationId}/?type=${flowType}`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to check flow');
    }
  },

  /**
   * Get complete flow configuration for a location
   */
  /**
   * Get all flow configurations for a location
   */
  async getFlows(locationId: string): Promise<Flow[]> {
    try {
      return await fetchWithAuth(`rest/flow-builder/locations/${locationId}/flow/`, {
        method: 'GET',
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return [];
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to get flows');
    }
  },

  /**
   * Create a new flow for a location from a template
   */
  async createFlow(locationId: string, flowType: FlowType): Promise<Flow> {
    try {
      return await fetchWithAuth(`rest/flow-builder/locations/${locationId}/flow/`, {
        method: 'POST',
        body: JSON.stringify({ type: flowType }),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create flow';

      if (errorMessage.includes('No flow builder config found')) {
        throw new Error(
          'Flow builder configuration is not set up. Please contact your administrator.'
        );
      }

      if (errorMessage.includes('not yet enrolled in the Lead Qualification beta')) {
        throw new Error(
          'This location is not yet enrolled in the Lead Qualification beta. Please contact support to request access.'
        );
      }

      try {
        const jsonMatch = errorMessage.match(/\{.*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.error) {
            // Check for specific backend configuration error
            if (parsed.error.includes('No flow builder config found')) {
              throw new Error(
                'Flow builder configuration is not set up. Please contact your administrator.'
              );
            }
            throw new Error(parsed.error);
          }
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Check if a landing flow exists for an organization and flow type
   */
  async checkLandingFlow(organizationId: string, flowType: FlowType): Promise<CheckFlowResponse> {
    try {
      return await fetchWithAuth(
        `rest/flow-builder/check-landing-flow/${organizationId}/?type=${flowType}`,
        {
          method: 'GET',
        }
      );
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to check landing flow');
    }
  },

  /**
   * Get all landing flow configurations for an organization
   */
  async getLandingFlows(organizationId: string): Promise<Flow[]> {
    try {
      return await fetchWithAuth(`rest/flow-builder/organizations/${organizationId}/flow/`, {
        method: 'GET',
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return [];
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to get landing flows');
    }
  },

  /**
   * Create a new landing flow for an organization from a template
   */
  async createLandingFlow(organizationId: string, flowType: FlowType): Promise<Flow> {
    try {
      return await fetchWithAuth(`rest/flow-builder/organizations/${organizationId}/flow/`, {
        method: 'POST',
        body: JSON.stringify({ type: flowType }),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create landing flow';

      if (errorMessage.includes('No flow builder config found')) {
        throw new Error(
          'Flow builder configuration is not set up. Please contact your administrator.'
        );
      }
      if (errorMessage.includes('not yet enrolled in the Lead Qualification beta')) {
        throw new Error(
          'This organization is not yet enrolled in the Lead Qualification beta. Please contact support to request access.'
        );
      }

      try {
        const jsonMatch = errorMessage.match(/\{.*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.error) {
            throw new Error(parsed.error);
          }
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get schedule settings for qualified leads
   */
  async getSchedule(flowId: number): Promise<Schedule> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/schedule/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get schedule');
    }
  },

  /**
   * Update schedule settings (supports partial updates)
   */
  async updateSchedule(flowId: number, updates: UpdateScheduleRequest): Promise<Schedule> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/schedule/`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update schedule';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Get rejection settings for unqualified leads
   */
  async getRejection(flowId: number): Promise<Rejection> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/rejection/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get rejection');
    }
  },

  /**
   * Update rejection settings (supports partial updates)
   */
  async updateRejection(flowId: number, updates: UpdateRejectionRequest): Promise<Rejection> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/rejection/`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update rejection';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * List all roles for a flow
   */
  async getRoles(flowId: number): Promise<Role[]> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/roles/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get roles');
    }
  },

  /**
   * Create a new role
   */
  async createRole(flowId: number, roleData: CreateRoleRequest): Promise<Role> {
    try {
      return await fetchWithAuth(`rest/flow-builder/flows/${flowId}/roles/`, {
        method: 'POST',
        body: JSON.stringify(roleData),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create role';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a single role with all its information
   */
  async getRole(roleId: number): Promise<Role> {
    try {
      return await fetchWithAuth(`rest/flow-builder/roles/${roleId}/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get role');
    }
  },

  /**
   * Duplicate a role (deep-copy, backend handled)
   */
  async duplicateRole(roleId: number): Promise<Role> {
    try {
      return await fetchWithAuth(`rest/flow-builder/roles/${roleId}/duplicate/`, {
        method: 'POST',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to duplicate role');
    }
  },

  /**
   * List all questions for a role
   */
  async getRoleQuestions(roleId: number): Promise<RoleInformationType[]> {
    try {
      return await fetchWithAuth(`rest/flow-builder/roles/${roleId}/questions/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get role questions');
    }
  },

  /**
   * Update a role (name, enabled, description, order)
   */
  async updateRole(roleId: number, updates: UpdateRoleRequest): Promise<Role> {
    try {
      const current = await flowBuilder.getRole(roleId);

      const payload: UpdateRoleRequest = {
        name: updates.name ?? current.name,
        enabled: updates.enabled ?? current.enabled,
        description: updates.description ?? current.description,
        order: updates.order ?? current.order,
      };

      return await fetchWithAuth(`rest/flow-builder/roles/${roleId}/`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update role';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a role and all its qualification questions
   */
  async deleteRole(roleId: number): Promise<void> {
    try {
      await fetchWithAuth(`rest/flow-builder/roles/${roleId}/`, {
        method: 'DELETE',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete role');
    }
  },

  /**
   * Get a single qualification question
   */
  async getQuestion(infoId: number): Promise<RoleInformationType> {
    try {
      return await fetchWithAuth(`rest/flow-builder/role-information/${infoId}/`, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get question');
    }
  },

  /**
   * Create a new qualification question
   */
  async createQuestion(
    roleId: number,
    questionData: CreateQuestionRequest
  ): Promise<RoleInformationType> {
    try {
      const payload: CreateQuestionRequest = {
        ...questionData,
        scoring: normalizeScoring(questionData.scoring),
        Icon: questionData.Icon || 'QuestionIcon',
        high_response: questionData.high_response || '',
        medium_response: questionData.medium_response || '',
        low_response: questionData.low_response || '',
        order: questionData.order ?? 0,
      };

      return await fetchWithAuth(`rest/flow-builder/roles/${roleId}/questions/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create question';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Update a qualification question
   */
  async updateQuestion(
    infoId: number,
    updates: UpdateQuestionRequest
  ): Promise<RoleInformationType> {
    try {
      const current = await flowBuilder.getQuestion(infoId);

      const payload: UpdateQuestionRequest = {
        scoring: normalizeScoring(updates.scoring ?? current.scoring),
        Icon: updates.Icon ?? (current.Icon || 'QuestionIcon'),
        question: updates.question ?? current.question,
        enabled: updates.enabled ?? current.enabled,
        answer_type: updates.answer_type ?? current.answer_type,
        upload: updates.upload ?? current.upload,
        description: updates.description ?? current.description,
        high_response: updates.high_response ?? current.high_response,
        medium_response: updates.medium_response ?? current.medium_response,
        low_response: updates.low_response ?? current.low_response,
        high_response_followup:
          updates.high_response_followup !== undefined
            ? updates.high_response_followup
            : current.high_response_followup,
        medium_response_followup:
          updates.medium_response_followup !== undefined
            ? updates.medium_response_followup
            : current.medium_response_followup,
        low_response_followup:
          updates.low_response_followup !== undefined
            ? updates.low_response_followup
            : current.low_response_followup,
        order: updates.order ?? current.order,
        role: current.role,
        choices: updates.choices ?? current.choices ?? [],
      };

      return await fetchWithAuth(`rest/flow-builder/role-information/${infoId}/`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update question';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed && typeof parsed === 'object') {
          const errorMessages = Object.entries(parsed)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          if (errorMessages) {
            throw new Error(errorMessages);
          }
        }
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a qualification question
   */
  async deleteQuestion(infoId: number): Promise<void> {
    try {
      await fetchWithAuth(`rest/flow-builder/role-information/${infoId}/`, {
        method: 'DELETE',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete question');
    }
  },

  /**
   * Save the current flow snapshot to its owning location/organization context.
   */
  async saveFlowToPreviewAgent(flowId: number, previewAgentId?: number): Promise<SaveFlowResponse> {
    try {
      return await fetchWithAuth(`rest/flow-builder/save/`, {
        method: 'POST',
        body: JSON.stringify({
          flow_id: flowId,
          ...(previewAgentId ? { preview_agent_id: previewAgentId } : {}),
        }),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save flow to preview agent';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Publish the current flow snapshot to its owning location/organization context.
   */
  async publishFlowToLiveAgent(
    flowId: number,
    agentId?: string | number
  ): Promise<PublishFlowResponse> {
    try {
      return await fetchWithAuth(`rest/flow-builder/publish/`, {
        method: 'POST',
        body: JSON.stringify({
          flow_id: flowId,
          ...(agentId ? { agent_id: agentId } : {}),
        }),
      });
    } catch (error) {
      throw normalizePublishError(error);
    }
  },

  /**
   * Get all unique role names across all flows
   */
  async getAllRoles(params?: {
    organization_ids?: string[];
    location_ids?: string[];
  }): Promise<string[]> {
    try {
      const queryParams: Record<string, string> = {};
      if (params?.organization_ids?.length) {
        queryParams.organization_ids = params.organization_ids.join(',');
      }
      if (params?.location_ids?.length) {
        queryParams.location_ids = params.location_ids.join(',');
      }

      const queryString = new URLSearchParams(queryParams).toString();
      const url = `rest/flow-builder/all-roles/${queryString ? `?${queryString}` : ''}`;

      return await fetchWithAuth(url, {
        method: 'GET',
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get all roles');
    }
  },
  /**
   * Atomically duplicate a flow to multiple locations
   */
  async duplicateFlow(flowId: number, targetIds: string[]): Promise<void> {
    try {
      await fetchWithAuth(`rest/flow-builder/duplicate/`, {
        method: 'POST',
        body: JSON.stringify({
          flow_id: flowId,
          target_ids: targetIds,
        }),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate flow';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Reset a flow to its default template configuration
   */
  async resetFlowToDefaults(flowId: number): Promise<Flow> {
    try {
      const response = await fetchWithAuth(`rest/flow-builder/flows/${flowId}/reset/`, {
        method: 'POST',
      });
      return response.flow;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset flow';
      // Try to parse error if it's JSON
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Toggle the active state of a flow.
   * When inactive, the PreviewAgent snapshot is cleared so agents don't see qualification questions.
   * When active, the flow is re-synced to the PreviewAgent snapshot.
   */
  async toggleFlowState(flowId: number, isActive: boolean): Promise<Flow> {
    try {
      const response = await fetchWithAuth(`rest/flow-builder/flows/${flowId}/toggle/`, {
        method: 'POST',
        body: JSON.stringify({ is_active: isActive }),
      });
      return response.flow;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle flow state';
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch {
        // Not JSON or parsing failed, use original message
      }
      throw new Error(errorMessage);
    }
  },
};

export default flowBuilder;
